import { useState } from 'react'

import styled from 'styled-components'

import { useDeleteStory } from 'api/stories'
import { IconStatsClose, IconThreeDots } from 'assets/svg/sprite'
import Report from 'components/UI/Modal/Report/Report'
import { StoryOptions } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { useReportStory } from 'helpers/hooks'
import { useUserProfile } from 'store'

interface StoryHeaderProps {
  date: Date
  avatar: string
  username: string
  id: string
  storyId: string
  withCloseButton: boolean
  onStoryDelete?: (storyId: string) => void
  onClose?: () => void
  onNavigateToProfile?: (username: string) => void
}

export const StoryHeader = ({
  date,
  avatar,
  username,
  id,
  storyId,
  withCloseButton,
  onStoryDelete,
  onClose,
  onNavigateToProfile,
}: StoryHeaderProps) => {
  const modalData = useModalContext()
  const { id: loggedInUserId } = useUserProfile()!

  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const { reportStoryMutation } = useReportStory()

  function handleClose() {
    // close fullscreen / modal (parent-controlled)
    onClose?.()

    // if this header is ever rendered inside modalContext, clear it too
    // harmless if there isn't one
    modalData.clearModal()
  }

  const { mutate } = useDeleteStory(() => {
    handleClose()
    onStoryDelete?.(storyId)
  })

  function handleNavigateToProfile() {
    onNavigateToProfile?.(username)
  }

  function handleOpenOptions() {
    setIsOptionsOpen(true)
  }

  function handleDelete() {
    mutate({ storyId, userId: id }) // id == creator/user id in this header
  }

  function handleReport() {
    setIsOptionsOpen(false)

    modalData.addModal(
      'Report Story',
      <Report
        type='content'
        confirmFn={(message: string, reportType: number) => {
          reportStoryMutation.mutate(
            { storyId, message, report_type_id: reportType },
            { onSuccess: () => modalData.clearModal() }
          )
        }}
        closeFn={() => modalData.clearModal()}
      />
    )
  }

  const generateTimeString = (d: Date) => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    const now = new Date()
    const diffMs = Math.abs(d.getTime() - now.getTime())
    const diffHours = diffMs / 36e5

    if (diffHours < 1) return `${Math.round(diffHours * 60)} minutes ago`
    if (diffHours < 24) return `${Math.floor(diffHours)} hours ago`

    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${monthNames[d.getMonth()]} ${d.getDate()}, ${hh}:${mm}`
  }

  return (
    <HeaderContainer>
      <ProgressContainer />
      <Header>
        <TitleContainer>
          <AvatarContainer onClick={handleNavigateToProfile}>
            <AvatarRing>
              <Avatar src={avatar} alt={`${username}'s avatar`} />
            </AvatarRing>
          </AvatarContainer>

          <Title onClick={handleNavigateToProfile}>{username}</Title>
          <Subtitle>{generateTimeString(date)}</Subtitle>
        </TitleContainer>

        <Delete onClick={handleOpenOptions}>
          <IconThreeDots />
          <StoryOptions
            isOpen={isOptionsOpen}
            setIsOpen={setIsOptionsOpen}
            handleReport={handleReport}
            handleDelete={handleDelete}
            isOwner={loggedInUserId === id}
            storyId={storyId}
          />
        </Delete>

        {withCloseButton && (
          <IconCloseWrapper onClick={handleClose}>
            <IconStatsClose />
          </IconCloseWrapper>
        )}
      </Header>
    </HeaderContainer>
  )
}


/* -------------------- styled components (unchanged) -------------------- */

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2rem;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 7;
  pointer-events: none;
    @media (min-width: 768px) {
    max-width: 600px;
    left: 50%;
    transform: translateX(-50%);
  }
`

const ProgressContainer = styled.div`
  height: 2rem;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const AvatarContainer = styled.div`
  align-items: center;
  border: 2px solid #2e97fe;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  height: 4rem;
  width: 4rem;
  pointer-events: all;
`

const AvatarRing = styled.div`
  width: 4.7rem;
  height: 4.7rem;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`

const Avatar = styled.img`
  border-radius: 50%;
  display: block;
  height: 3.6rem;
  width: 3.6rem;
  object-fit: cover;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`

const Title = styled.div`
  font-size: 1.8rem;
  line-height: 2.2rem;
  margin-right: 1rem;
  color: #ffffff;
  margin-left: 1.3rem;
  font-weight: 600;
  pointer-events: all;
  padding: 0.2rem 0.5rem;
`

const Subtitle = styled.div`
  font-size: 1.2rem;
  line-height: 1.5rem;
  color: #bfbebe;
`

const IconCloseWrapper = styled.a`
  pointer-events: all;
`

const Delete = styled.div`
  pointer-events: all;
  margin-left: auto;
  margin-right: 3rem;
`
